/**
 * VerifyModal.jsx — Phase 12 "زر تحقّق بنفسك"
 *
 * Opens a modal that fetches 5 RANDOM raw rows from pos_sales for the
 * clicked measured metric, alongside the formula applied to just those
 * 5 rows and a sentence explaining how they aggregate to the shown
 * number. Random on every open — so no one can accuse cherry-picking.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { zVar } from "../lib/zIndex";
import { formatKSADateTime } from "../lib/ksaTime";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

const HEADERS_BY_METRIC = {
  aov_paid_window: [
    { key: "customer_name",  label: "الاسم" },
    { key: "customer_phone", label: "الجوال", mono: true },
    { key: "product_name",   label: "المنتج" },
    { key: "total_price",    label: "قيمة الفاتورة", num: true, suffix: " ر" },
    { key: "created_at",     label: "التاريخ", date: true },
  ],
  net_sales: [
    { key: "customer_name",  label: "الاسم" },
    { key: "product_name",   label: "المنتج" },
    { key: "total_price",    label: "المبلغ", num: true, suffix: " ر" },
    { key: "created_at",     label: "التاريخ", date: true },
  ],
  invoice_count: [
    { key: "customer_phone", label: "الجوال", mono: true },
    { key: "total_price",    label: "قيمة الفاتورة", num: true, suffix: " ر" },
    { key: "created_at",     label: "التاريخ", date: true },
  ],
  conversion_rate_paid: [
    { key: "customer_name",  label: "الاسم" },
    { key: "customer_phone", label: "الجوال", mono: true },
    { key: "visits",         label: "زيارات", num: true },
    { key: "total_paid_sar", label: "المدفوع", num: true, suffix: " ر" },
    { key: "is_paid",        label: "اشترى مدفوعًا؟", bool: true },
  ],
  retention_30d: [
    { key: "customer_name",  label: "الاسم" },
    { key: "customer_phone", label: "الجوال", mono: true },
    { key: "last_visit_prev_window",      label: "زيارة الفترة السابقة", date: true },
    { key: "returned_in_current_window",  label: "عاد الفترة الحالية؟", bool: true },
  ],
  retention_60d: null, // reuse retention_30d layout
  retention_90d: null,
  customer_visits: [
    { key: "customer_name",  label: "الاسم" },
    { key: "customer_phone", label: "الجوال", mono: true },
    { key: "visits",         label: "زيارات", num: true },
    { key: "spent_sar",      label: "الإنفاق", num: true, suffix: " ر" },
    { key: "aov_sar",        label: "AOV", num: true, suffix: " ر" },
    { key: "last_visit",     label: "آخر زيارة", date: true },
  ],
  customer_spent:   null, customer_aov: null, customer_recency: null,
  unique_customers: null,
  segment_counts: [
    { key: "customer_name",  label: "الاسم" },
    { key: "customer_phone", label: "الجوال", mono: true },
    { key: "visits",         label: "زيارات", num: true },
    { key: "spent_sar",      label: "الإنفاق", num: true, suffix: " ر" },
  ],
  segment_percentages: null,
  weekly_trend_deltas: [
    { key: "customer_name",  label: "الاسم" },
    { key: "customer_phone", label: "الجوال", mono: true },
    { key: "product_name",   label: "المنتج" },
    { key: "total_price",    label: "المبلغ", num: true, suffix: " ر" },
    { key: "created_at",     label: "التاريخ", date: true },
  ],
  segment_movement_counts: null,
  launch_cohort_completed: [
    { key: "customer_name",  label: "الاسم" },
    { key: "product_name",   label: "المنتج" },
    { key: "total_price",    label: "المدفوع", num: true, suffix: " ر" },
    { key: "discount_amount", label: "الخصم", num: true, suffix: " ر" },
    { key: "created_at",     label: "التاريخ", date: true },
  ],
  launch_cohort_partial: null,
  launch_cohort_registered: null,
  launch_free_cups: null,
  launch_forgone_revenue: null,
  launch_paid_revenue_after: null,
  launch_converts: null,
  launch_cogs: null,
};

// Resolve nulls to their canonical layout partner
function resolveHeaders(metricId) {
  const alias = {
    retention_60d: "retention_30d", retention_90d: "retention_30d",
    customer_spent: "customer_visits", customer_aov: "customer_visits",
    customer_recency: "customer_visits", unique_customers: "customer_visits",
    segment_percentages: "segment_counts",
    segment_movement_counts: "weekly_trend_deltas",
    launch_cohort_partial: "launch_cohort_completed",
    launch_cohort_registered: "launch_cohort_completed",
    launch_free_cups: "launch_cohort_completed",
    launch_forgone_revenue: "launch_cohort_completed",
    launch_paid_revenue_after: "launch_cohort_completed",
    launch_converts: "launch_cohort_completed",
    launch_cogs: "launch_cohort_completed",
  };
  return HEADERS_BY_METRIC[metricId] || HEADERS_BY_METRIC[alias[metricId]] || null;
}

function renderCell(row, h) {
  const v = row?.[h.key];
  if (v == null || v === "") return "—";
  if (h.bool) return v ? "✅ نعم" : "❌ لا";
  if (h.date) {
    try {
      const d = new Date(v);
      return formatKSADateTime(d, {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return String(v); }
  }
  if (h.num) return fmt(v) + (h.suffix || "");
  return String(v);
}

export default function VerifyModal({ metricId, onClose }) {
  const [state, setState] = useState({ loading: true, err: null, data: null });
  const [nonce, setNonce] = useState(0);  // increments to force new sample

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, err: null, data: null });
    api.get("/customer-metrics/verify-sample", { params: { metric_id: metricId } })
      .then((r) => { if (!cancelled) setState({ loading: false, err: null, data: r.data }); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e?.message || "خطأ";
        setState({ loading: false, err: msg, data: null });
      });
    return () => { cancelled = true; };
  }, [metricId, nonce]);

  const headers = resolveHeaders(metricId);
  const rows = state.data?.rows || [];
  const formula = state.data?.formula_applied || {};
  const explain = state.data?.explain_ar;

  return (
    <div
      data-testid="verify-modal"
      className="vm-wrap"
      dir="rtl"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, zIndex: zVar('extra_9999'),
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "60px 20px", overflowY: "auto",
        fontFamily: "'El Messiri', -apple-system, 'Noto Naskh Arabic', sans-serif",
        fontVariantNumeric: "lining-nums tabular-nums",
      }}
    >
      <div
        className="vm-box"
        style={{
          width: "100%", maxWidth: 780, background: "#FFFFFF",
          borderRadius: 20, boxShadow: "0 20px 60px rgba(15,23,42,0.35)",
          padding: "24px 26px 22px",
        }}
      >
        <header style={{
          display: "flex", justifyContent: "space-between", alignItems: "start",
          gap: 12, marginBottom: 14,
        }}>
          <div>
            <div style={{ fontSize: 11, color: "#0F766E", fontWeight: 700, letterSpacing: 0.5 }}>
              🔍 تحقّق بنفسك · 5 أسطر خام من pos_sales
            </div>
            <h2 style={{ margin: "4px 0 0", fontSize: 20, fontWeight: 700, color: "#3D2A1C" }}>
              <code style={{
                background: "rgba(170,149,102,0.14)", padding: "2px 8px",
                borderRadius: 6, fontFamily: "monospace", fontSize: 15,
              }}>{metricId}</code>
            </h2>
          </div>
          <button
            data-testid="verify-modal-close"
            onClick={onClose}
            style={{
              border: "none", cursor: "pointer",
              width: 34, height: 34, borderRadius: 999,
              background: "#F7F2EC", color: "#3D2A1C",
              fontSize: 18, fontWeight: 700,
            }}
            aria-label="إغلاق"
          >×</button>
        </header>

        {state.loading && (
          <div data-testid="verify-loading" style={{
            padding: 40, textAlign: "center", color: "#78716C",
          }}>جارٍ سحب عيّنة عشوائية…</div>
        )}

        {state.err && (
          <div data-testid="verify-error" style={{
            padding: 16, borderRadius: 12,
            background: "#FEF3F2", color: "#B91C1C",
            border: "1px solid rgba(185,28,28,0.35)",
          }}>{state.err}</div>
        )}

        {!state.loading && !state.err && (
          <>
            {rows.length === 0 ? (
              <div data-testid="verify-empty" style={{
                padding: 20, borderRadius: 12,
                background: "#FFFBEB", color: "#92400E",
                border: "1px solid rgba(180,83,9,0.35)", fontSize: 13,
              }}>
                لا توجد بيانات كافية لعيّنة هذا المقياس في نافذة 90 يومًا.
              </div>
            ) : headers ? (
              <div data-testid="verify-table-wrap" className="vm-table-wrap" style={{ overflowX: "auto" }}>
                <table className="vm-table" style={{
                  width: "100%", borderCollapse: "separate", borderSpacing: 0,
                  fontSize: 12, background: "#FAFAF7",
                  borderRadius: 12, overflow: "hidden",
                }}>
                  <thead>
                    <tr style={{ background: "rgba(170,149,102,0.14)" }}>
                      {headers.map((h) => (
                        <th key={h.key} style={{
                          padding: "10px 12px", textAlign: "start",
                          fontSize: 11, fontWeight: 700, color: "#57534E",
                        }}>{h.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} data-testid={`verify-row-${i}`} style={{
                        borderTop: "1px solid rgba(61,42,28,0.06)",
                      }}>
                        {headers.map((h) => (
                          <td key={h.key} style={{
                            padding: "10px 12px", color: "#3D2A1C",
                            fontFamily: h.mono ? "monospace" : "inherit",
                            fontSize: h.mono ? 11 : 12,
                          }}>
                            {renderCell(r, h)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#78716C" }}>
                عيّنة خام:
                <pre style={{ fontSize: 11, overflowX: "auto",
                              background: "#F7F2EC", padding: 12,
                              borderRadius: 8 }}>
                  {JSON.stringify(rows, null, 2)}
                </pre>
              </div>
            )}

            {Object.keys(formula).length > 0 && (
              <div data-testid="verify-formula" style={{
                marginTop: 14, padding: "12px 14px", borderRadius: 12,
                background: "rgba(15,118,110,0.08)",
                border: "1px solid rgba(15,118,110,0.30)",
                fontSize: 12, color: "#0F766E",
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  📐 المعادلة مطبَّقة على هذه العيّنة:
                </div>
                <ul style={{ margin: 0, paddingInlineStart: 20, lineHeight: 1.9 }}>
                  {Object.entries(formula).map(([k, v]) => (
                    <li key={k}>
                      <code style={{ fontFamily: "monospace", fontSize: 11 }}>{k}</code>
                      {" = "}
                      <b>{typeof v === "number" ? fmt(v) : String(v)}</b>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {explain && (
              <div data-testid="verify-explain" style={{
                marginTop: 12, padding: "12px 14px", borderRadius: 12,
                background: "#F7F2EC", color: "#57534E",
                fontSize: 12, lineHeight: 1.8,
              }}>
                💡 {explain}
              </div>
            )}
          </>
        )}

        <footer className="vm-footer" style={{
          marginTop: 18, display: "flex", justifyContent: "space-between",
          alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <div style={{ fontSize: 11, color: "#A8A29E" }}>
            العينة عشوائية في كل نقرة — لا اختيار انتقائي.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              data-testid="verify-resample"
              onClick={() => setNonce((n) => n + 1)}
              disabled={state.loading}
              style={{
                border: "none", cursor: "pointer",
                padding: "8px 14px", borderRadius: 10, fontSize: 12,
                fontWeight: 700, background: "#0F766E", color: "#FFFFFF",
              }}
            >🎲 اسحب 5 عيّنات أخرى</button>
            <button
              data-testid="verify-modal-done"
              onClick={onClose}
              style={{
                border: "1px solid rgba(61,42,28,0.14)", cursor: "pointer",
                padding: "8px 14px", borderRadius: 10, fontSize: 12,
                fontWeight: 700, background: "#FFFFFF", color: "#3D2A1C",
              }}
            >تم</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
